import { 
    Search, FileText, LayoutTemplate, Briefcase, Users, Navigation, ExternalLink, 
    MessageSquare, MapPin, UserCheck, CheckCircle, Building, Calendar, Clock, 
    UserPlus, DollarSign, Activity, BookOpen, Settings, Shield, Star, Mail, Phone,
    Sparkles, HelpCircle, Bot, Megaphone, Bell, BarChart3, Zap, Cake
} from 'lucide-react';

/**
 * QUICK SEARCH CATALOG - FIT TOUR ERP
 * Danh mục chuẩn hóa toàn bộ Thao tác nhanh, Module, Tính năng con, Tài liệu & SOP
 * Hỗ trợ bộ từ khóa đa dạng: tiếng Việt có dấu, không dấu, từ lóng công sở, viết tắt chuyên ngành.
 */

// ── 1. THAO TÁC NHANH (ACTION SHORTCUTS - TIER 1) ──
export const actionShortcuts = [
    {
        id: 'action-create-leave',
        title: '⚡ Tạo đơn xin nghỉ phép',
        subtitle: 'Mở popup nộp đơn xin nghỉ phép, nghỉ ốm, việc riêng, bù giờ',
        type: 'action',
        action: 'open-add-leave-modal',
        category: 'action',
        badge: 'Thao tác nhanh',
        icon: Zap,
        keywords: 'tao don xin nghi phep off vang mat nghi om nghi thai san viec rieng phep nam bu gio bao ban nghi phep leave request'
    },
    {
        id: 'action-create-lead',
        title: '⚡ Thêm khách hàng tiềm năng (Lead)',
        subtitle: 'Tạo Lead mới nhận từ hotline, messenger, zalo hoặc trực tiếp',
        type: 'action',
        action: 'open-add-lead-modal',
        category: 'action',
        badge: 'Thao tác nhanh',
        icon: Users,
        keywords: 'them moi lead khach tiem nang sdt so dien thoai inbox tao lead nhan lead hot lead'
    },
    {
        id: 'action-create-booking',
        title: '⚡ Tạo đơn đặt chỗ (Booking)',
        subtitle: 'Tạo booking mới cho khách lẻ hoặc đoàn, giữ chỗ tour',
        type: 'action',
        action: 'open-add-booking-modal',
        category: 'action',
        badge: 'Thao tác nhanh',
        icon: Briefcase,
        keywords: 'tao don hang booking dat cho dat tour giu cho hop dong crm'
    },
    {
        id: 'action-create-customer',
        title: '⚡ Thêm khách hàng mới',
        subtitle: 'Nhập thông tin hồ sơ khách hàng mới vào CRM',
        type: 'action',
        action: 'open-add-customer-modal',
        category: 'action',
        badge: 'Thao tác nhanh',
        icon: UserPlus,
        keywords: 'them moi khach hang tao customer crm thong tin khach profile'
    },
    {
        id: 'action-scan-passport',
        title: '⚡ Quét hộ chiếu (Passport OCR)',
        subtitle: 'Quét và trích xuất thông tin passport tự động qua AI/OCR',
        path: '/passport-ocr',
        type: 'action',
        category: 'action',
        badge: 'Thao tác nhanh',
        icon: FileText,
        keywords: 'quet passport ocr ho chieu scan tu dong chup anh doc thong tin visa'
    },
    {
        id: 'action-book-meeting-room',
        title: '⚡ Đặt phòng họp nhanh',
        subtitle: 'Kiểm tra lịch trống và đăng ký phòng họp nội bộ công ty',
        path: '/meeting-rooms',
        type: 'action',
        category: 'action',
        badge: 'Thao tác nhanh',
        icon: Building,
        keywords: 'dat phong hop nhanh meeting rooms phong hop lich hop giu phong'
    }
];

// ── 2. MODULE & TÍNH NĂNG CHÍNH (CORE MODULES - TIER 2) ──
export const coreModules = [
    // ── Khối Nhân sự & Văn phòng ──
    {
        id: 'leaves',
        title: 'Quản lý Nghỉ phép (Leaves)',
        subtitle: 'Bảng theo dõi phép năm, duyệt đơn nghỉ phép, lịch sử vắng mặt',
        path: '/leaves',
        icon: Clock,
        category: 'module',
        deptBadge: 'Nhân sự',
        keywords: 'quan ly nghi phep leaves don xin phep ngay nghi phep nam phep ton bang tinh phep duyet phep off nghi om nghi thai san phep bu quy che'
    },
    {
        id: 'staff-calendar',
        title: 'Lịch làm việc nhân viên (Staff Calendar)',
        subtitle: 'Lịch công tác, ngày nghỉ phép toàn công ty, ca làm việc',
        path: '/staff-calendar',
        icon: Calendar,
        category: 'module',
        deptBadge: 'Nhân sự',
        keywords: 'lich lam viec nhan vien staff calendar ca lam truc nghi phep toan cong ty lich nghi le ngay nghi cham cong bang cong ai nghi hom nay'
    },
    {
        id: 'staff-calendar-birthday',
        title: '🎂 Sinh nhật nhân viên trong tháng',
        subtitle: 'Xem danh sách ngày sinh nhật của đồng nghiệp trong tháng trên Lịch làm việc',
        path: '/staff-calendar?filter=birthday',
        icon: Cake,
        category: 'module',
        deptBadge: 'Nhân sự',
        keywords: 'sinh nhat nhan vien birthday dong nghiep sn tiec sn mung tuoi ngay sinh staff calendar ban be cty van hoa'
    },
    {
        id: 'team-directory',
        title: 'Danh bạ nhân viên FIT Tour',
        subtitle: 'Tra cứu số điện thoại, email, chức vụ, bộ phận đồng nghiệp',
        path: '/team-directory',
        icon: Phone,
        category: 'module',
        deptBadge: 'Nhân sự',
        keywords: 'danh ba nhan vien fit tour so dien thoai email lien he sdt dong nghiep team directory hotline noi bo phong ban'
    },
    {
        id: 'meeting-rooms',
        title: 'Phòng họp (Meeting Rooms)',
        subtitle: 'Lịch sử dụng và đăng ký phòng họp công ty FIT Tour',
        path: '/meeting-rooms',
        icon: Building,
        category: 'module',
        deptBadge: 'Văn phòng',
        keywords: 'phong hop meeting rooms dat phong lich hop thiet bi may chieu phong training'
    },
    {
        id: 'org-chart',
        title: 'Sơ đồ tổ chức (Org Chart)',
        subtitle: 'Cơ cấu phòng ban, cây nhân sự, lãnh đạo các khối BU',
        path: '/org-chart',
        icon: LayoutTemplate,
        category: 'module',
        deptBadge: 'Nhân sự',
        keywords: 'so do to chuc org chart phong ban co cau nhan su sep truong phong bum quan ly'
    },
    {
        id: 'users',
        title: 'Quản trị Người dùng & Phân quyền',
        subtitle: 'Cấp tài khoản nhân viên, phân quyền truy cập, thiết lập mật khẩu',
        path: '/users',
        icon: UserPlus,
        category: 'module',
        deptBadge: 'Admin',
        requiredRole: 'admin',
        keywords: 'quan tri nguoi dung users tai khoan phan quyen admin pass mat khau role cap quyen khoa tai khoan'
    },
    {
        id: 'my-profile',
        title: 'Hồ sơ cá nhân & Đổi mật khẩu',
        subtitle: 'Thông tin tài khoản, avatar, biệt danh, đổi password đăng nhập',
        path: '/my-profile',
        icon: Users,
        category: 'module',
        deptBadge: 'Cá nhân',
        keywords: 'ho so ca nhan my profile thong tin doi mat khau password email ca nhan avatar nickname'
    },
    {
        id: 'notification-center',
        title: 'Trung tâm Thông báo',
        subtitle: 'Cảnh báo hệ thống, thông báo duyệt đơn, phân bổ lead mới',
        path: '/notification-center',
        icon: Bell,
        category: 'module',
        deptBadge: 'Hệ thống',
        keywords: 'trung tam thong bao notification center tin moi chuong alerts tin nhan he thong thong bao nghi phep'
    },
    {
        id: 'notification-dashboard',
        title: 'Dashboard Thông báo',
        subtitle: 'Thống kê lượng gửi, tỷ lệ đọc, metrics thông báo toàn hệ thống',
        path: '/notification-dashboard',
        icon: LayoutTemplate,
        category: 'module',
        deptBadge: 'Hệ thống',
        keywords: 'dashboard thong bao notification metrics thong ke ti le doc alerts'
    },

    // ── Khối Kinh doanh, CSKH & Khách hàng ──
    {
        id: 'leads',
        title: 'Quản lý Lead (Nguồn cơ hội)',
        subtitle: 'Tiếp nhận, xử lý, gắn tag và phân bổ khách hàng tiềm năng',
        path: '/leads',
        icon: Users,
        category: 'module',
        deptBadge: 'Kinh doanh',
        keywords: 'quan ly lead nguon co hoi khach tiem nang sdt so dien thoai inbox co sdt chia lead nhan lead pipeline sale ban hang'
    },
    {
        id: 'leads-dashboard',
        title: 'Dashboard Lead & Hiệu suất',
        subtitle: 'Phân tích phễu chuyển đổi, tỷ lệ chốt deal, phân bổ theo BU',
        path: '/leads-dashboard',
        icon: LayoutTemplate,
        category: 'module',
        deptBadge: 'Kinh doanh',
        keywords: 'dashboard lead ty le chuyen doi conversion phan bo lead kpi phieu ban hang sale funnel'
    },
    {
        id: 'workspace',
        title: 'Workspace: Bàn làm việc Sales',
        subtitle: 'Danh sách việc cần làm, nhắc nhở khách hẹn, sinh nhật khách',
        path: '/workspace',
        icon: LayoutTemplate,
        category: 'module',
        deptBadge: 'Kinh doanh',
        keywords: 'workspace ban lam viec todo viec can lam nhiem vu ca nhan sinh nhat khach hang upcoming birthday lich hen sale'
    },
    {
        id: 'customers',
        title: 'Danh sách Khách hàng (CRM)',
        subtitle: 'Hồ sơ khách hàng, phân tầng VIP 1-3, lịch sử tour và sinh nhật',
        path: '/customers',
        icon: Users,
        category: 'module',
        deptBadge: 'CSKH',
        keywords: 'danh sach khach hang crm thong tin khach cskh tra cuu lich su di tour sinh nhat khach birthday vip phan tang loyalty'
    },
    {
        id: 'bookings',
        title: 'Quản lý Đơn hàng (Bookings)',
        subtitle: 'Theo dõi hợp đồng, tiền cọc, bảo lưu cọc, tình trạng giữ chỗ',
        path: '/bookings',
        icon: Briefcase,
        category: 'module',
        deptBadge: 'Kinh doanh',
        keywords: 'quan ly don hang bookings dat tour hop dong thanh toan giu cho coc tien coc bao luu split booking'
    },
    {
        id: 'cskh-board',
        title: 'Chăm sóc Khách hàng (CSKH Board)',
        subtitle: 'Khảo sát sau tour, xử lý phản hồi, quà tặng tri ân và sinh nhật',
        path: '/cskh-board',
        icon: MessageSquare,
        category: 'module',
        deptBadge: 'CSKH',
        keywords: 'cskh board cham soc khach hang sau tour truoc tour khao sat phan nan danh gia review sinh nhat khach tang qua tri an'
    },
    {
        id: 'cskh-birthday',
        title: '🎁 Sinh nhật khách hàng sắp tới (CSKH)',
        subtitle: 'Danh sách khách hàng VIP có sinh nhật trong tuần/tháng để gửi lời chúc & quà',
        path: '/cskh-board',
        icon: Cake,
        category: 'module',
        deptBadge: 'CSKH',
        keywords: 'sinh nhat khach hang cskh chuc mung sinh nhat birthday upcoming tri an khach vip nhac nho sinh nhat qua tang'
    },
    {
        id: 'cskh-todo',
        title: 'Công việc CSKH (To-do)',
        subtitle: 'Checklist các nhiệm vụ chăm sóc khách hàng cần xử lý trong ngày',
        path: '/cskh-todo',
        icon: CheckCircle,
        category: 'module',
        deptBadge: 'CSKH',
        keywords: 'cong viec cskh todo viec can lam cham soc khach checklist lien he khao sat'
    },
    {
        id: 'cskh-search',
        title: 'Tra cứu CSKH',
        subtitle: 'Tìm kiếm nhanh lịch sử cuộc gọi, trao đổi và khiếu nại của khách',
        path: '/cskh-search',
        icon: Search,
        category: 'module',
        deptBadge: 'CSKH',
        keywords: 'tra cuu cskh tim kiem khach hang lich su cham soc thong tin cuoc goi ghi chu'
    },
    {
        id: 'cskh-rules',
        title: 'Cấu hình Rules CSKH (Tự động hóa)',
        subtitle: 'Quy tắc tự động kích hoạt chúc mừng sinh nhật, gửi tin nhắn cảm ơn',
        path: '/customers/cskh-rules',
        icon: Settings,
        category: 'module',
        deptBadge: 'CSKH',
        keywords: 'cau hinh rules cskh quy tac cham soc tu dong birthday upcoming sinh nhat tu dong gui tin nhan tri an'
    },
    {
        id: 'staff-performance',
        title: 'Hiệu suất Nhân viên (Sales KPI)',
        subtitle: 'Báo cáo năng suất cá nhân, doanh số chốt tour, hoa hồng commission',
        path: '/staff-performance',
        icon: Activity,
        category: 'module',
        deptBadge: 'Kinh doanh',
        keywords: 'hieu suat nhan vien kpi nang suat doanh so sale doanh thu commission hoa hong thuong kpi'
    },

    // ── Khối Tour Đoàn & Doanh nghiệp (MICE & B2B) ──
    {
        id: 'group-dashboard',
        title: 'Dashboard MICE (Tour Đoàn Doanh Nghiệp)',
        subtitle: 'Tổng quan dự án đoàn công ty, teambuilding, gala dinner',
        path: '/group/dashboard',
        icon: LayoutTemplate,
        category: 'module',
        deptBadge: 'MICE',
        keywords: 'dashboard mice tour doan doanh nghiep cong ty teambuilding year end party gala trip'
    },
    {
        id: 'b2b-companies',
        title: 'Công ty Đối tác (B2B)',
        subtitle: 'Quản lý danh sách các đại lý du lịch, đối tác liên minh gửi khách',
        path: '/group/companies',
        icon: Building,
        category: 'module',
        deptBadge: 'MICE',
        keywords: 'cong ty doi tac b2b dai ly doanh nghiep to chuc khach doan consortium lien minh'
    },
    {
        id: 'companies',
        title: 'Doanh nghiệp & Khách hàng Tổ chức',
        subtitle: 'Hồ sơ pháp nhân doanh nghiệp, mã số thuế, thông tin xuất VAT',
        path: '/companies',
        icon: Building,
        category: 'module',
        deptBadge: 'MICE',
        keywords: 'doanh nghiep cong ty b2b khach hang to chuc mst ma so thue xuat hoa don vat'
    },
    {
        id: 'group-leaders',
        title: 'Trưởng đoàn (Group Leaders)',
        subtitle: 'Hồ sơ các trưởng đoàn đại diện doanh nghiệp, sinh nhật trưởng đoàn',
        path: '/group/leaders',
        icon: Users,
        category: 'module',
        deptBadge: 'MICE',
        keywords: 'truong doan group leaders dai dien doan sinh nhat truong doan anniversary ngay thanh lap cong ty'
    },
    {
        id: 'group-projects',
        title: 'Dự án Tour Đoàn (Projects)',
        subtitle: 'Quản lý dự thầu, hồ sơ đề xuất (proposal), báo giá tour đoàn',
        path: '/group/projects',
        icon: Briefcase,
        category: 'module',
        deptBadge: 'MICE',
        keywords: 'du an tour doan group projects dau thau bao gia proposal hop dong doan pitch deck'
    },
    {
        id: 'group-mice-leads',
        title: 'MICE Leads (Cơ hội Tour Đoàn)',
        subtitle: 'Yêu cầu báo giá tour đoàn từ website, hotline hoặc hội thảo',
        path: '/group/mice-leads',
        icon: Users,
        category: 'module',
        deptBadge: 'MICE',
        keywords: 'mice leads tour doan yeu cau co hoi bao gia doan khach cong ty'
    },
    {
        id: 'marketing-google-ads',
        title: 'Báo Cáo Google Ads (BU3 B2B)',
        subtitle: 'Số liệu chuyển đổi Google Ads, hotline, Zalo cho tour doanh nghiệp',
        path: '/group/google-ads',
        icon: Search,
        category: 'module',
        deptBadge: 'Marketing',
        keywords: 'google ads search quang cao bu3 b2b tour doan doanh nghiep ga4 hotline zalo'
    },

    // ── Khối Điều hành & Hướng dẫn viên (Operations & Guides) ──
    {
        id: 'tours',
        title: 'Mẫu Chương trình Tour (Templates)',
        subtitle: 'Kho hành trình tour mẫu, chương trình chi tiết, điểm tham quan',
        path: '/tours',
        icon: MapPin,
        category: 'module',
        deptBadge: 'Điều hành',
        keywords: 'mau chuong trinh tour templates lich trinh tuyen diem itinerary brochure thiet ke tour'
    },
    {
        id: 'departures',
        title: 'Ngày Khởi hành (Departures)',
        subtitle: 'Lịch mở bán tour, số chỗ còn trống, ngày bay, danh sách đoàn',
        path: '/departures',
        icon: Navigation,
        category: 'module',
        deptBadge: 'Điều hành',
        keywords: 'ngay khoi hanh departures lich tour lich khoi hanh open tour ngay di cho trong slot'
    },
    {
        id: 'dispatch-schedule',
        title: 'Lịch Điều phối Tour (Dispatch)',
        subtitle: 'Phân công Tour Leader, hướng dẫn viên phụ trách từng đoàn',
        path: '/dispatch-schedule',
        icon: Calendar,
        category: 'module',
        deptBadge: 'Điều hành',
        keywords: 'lich dieu phoi dispatch tour phan bo tour leader hdv huong dan vien lich tour'
    },
    {
        id: 'bus',
        title: 'Quản lý Xe Bus & Sơ đồ ghế',
        subtitle: 'Sắp xếp xe vận chuyển, sơ đồ chỗ ngồi cho khách và trưởng đoàn',
        path: '/bus',
        icon: Navigation,
        category: 'module',
        deptBadge: 'Điều hành',
        keywords: 'quan ly xe bus van chuyen tuyen xe so do ghe tai xe nha xe vi tri ngoi'
    },
    {
        id: 'guides',
        title: 'Danh sách Hướng dẫn viên',
        subtitle: 'Hồ sơ HDV, Tour Leader, ngoại ngữ, chứng chỉ hành nghề, thẻ HDV',
        path: '/guides',
        icon: UserCheck,
        category: 'module',
        deptBadge: 'Điều hành',
        keywords: 'danh sach huong dan vien hdv tour leader ho so hdv bang lai the huong dan ngoai ngu'
    },
    {
        id: 'customer-reviews',
        title: 'Đánh giá của Khách hàng (Reviews 5 Sao)',
        subtitle: 'Theo dõi review Google Maps, feedback sau tour, tính thưởng HDV',
        path: '/guides/reviews',
        icon: Star,
        category: 'module',
        deptBadge: 'CSKH',
        keywords: 'danh gia khach hang reviews feedback google maps star sao chat luong dich vu khen che thuong hdv 100k 150k'
    },
    {
        id: 'op-tours',
        title: 'Điều hành Tour (OP)',
        subtitle: 'Vận hành dịch vụ, đặt cọc đối tác, quản lý chi phí phát sinh và quyết toán',
        path: '/op-tours',
        icon: Navigation,
        category: 'module',
        deptBadge: 'Điều hành',
        keywords: 'dieu hanh op tour van hanh dich vu tour leader ncc cong no quyet toan tour chi phi tour'
    },
    {
        id: 'costings',
        title: 'Bảng tính giá Tour (Costings)',
        subtitle: 'Dự toán chi phí giá thành, giá net đối tác, biên lợi nhuận tour',
        path: '/costings',
        icon: DollarSign,
        category: 'module',
        deptBadge: 'Điều hành',
        keywords: 'bang tinh gia tour costings chi phi gia loi nhuan budget du toan gia von gia ban markup'
    },
    {
        id: 'reminders',
        title: 'Nhắc nhở công việc (Reminders)',
        subtitle: 'Lịch nhắc hẹn visa, deadline thanh toán nhà cung cấp, xuất vé',
        path: '/reminders',
        icon: Clock,
        category: 'module',
        deptBadge: 'Điều hành',
        keywords: 'nhac nho cong viec reminders deadline lich hen task han chot bao han'
    },
    {
        id: 'passport-ocr',
        title: 'Quét Passport Hộ chiếu (OCR)',
        subtitle: 'Công cụ AI trích xuất tự động họ tên, ngày sinh, số hộ chiếu',
        path: '/passport-ocr',
        icon: FileText,
        category: 'module',
        deptBadge: 'Điều hành',
        keywords: 'quet passport ocr ho chieu scan quet anh tu dong doc ho chieu xuat file danh sach doan'
    },

    // ── Khối Nhà cung cấp (Suppliers - NCC) ──
    {
        id: 'hotels',
        title: 'NCC: Khách sạn & Resort (Hotels)',
        subtitle: 'Hồ sơ khách sạn, giá hợp đồng, loại phòng, thông tin liên hệ',
        path: '/hotels',
        icon: Building,
        category: 'module',
        deptBadge: 'Đối tác',
        keywords: 'ncc khach san hotels resort dat phong nghi luu tru khach san sao check in'
    },
    {
        id: 'restaurants',
        title: 'NCC: Nhà hàng & Ẩm thực (Restaurants)',
        subtitle: 'Danh sách nhà hàng ăn uống, thực đơn set menu, buffet theo tuyến',
        path: '/restaurants',
        icon: Building,
        category: 'module',
        deptBadge: 'Đối tác',
        keywords: 'ncc nha hang restaurants an uong dat ban buffet am thuc thuc don suat an menu'
    },
    {
        id: 'transports',
        title: 'NCC: Xe & Vận chuyển (Transports)',
        subtitle: 'Đội xe du lịch 16-29-45 chỗ, đối tác vận chuyển nội địa & quốc tế',
        path: '/transports',
        icon: Navigation,
        category: 'module',
        deptBadge: 'Đối tác',
        keywords: 'ncc van chuyen transports xe thue xe oto xe du lich xe 16 29 45 cho nha xe'
    },
    {
        id: 'airlines',
        title: 'NCC: Vé máy bay & Hàng không (Airlines)',
        subtitle: 'Đại lý vé máy bay, hãng hàng không, chính sách giữ chỗ và phạt đổi',
        path: '/airlines',
        icon: Navigation,
        category: 'module',
        deptBadge: 'Đối tác',
        keywords: 'ncc ve may bay airlines hang khong booking ve code ve gia ve chang bay ve doan'
    },
    {
        id: 'tickets',
        title: 'NCC: Vé tham quan & Điểm đến (Tickets)',
        subtitle: 'Voucher vé vào cổng các khu danh thắng, cáp treo, công viên giải trí',
        path: '/tickets',
        icon: FileText,
        category: 'module',
        deptBadge: 'Đối tác',
        keywords: 'ncc ve tham quan tickets voucher ve vao cong cap treo diem du lich show diễn'
    },
    {
        id: 'landtours',
        title: 'NCC: Land Tour & Đối tác địa phương',
        subtitle: 'Đối tác Land Tour tại Trung Quốc, Tây Á, Đông Bắc Á, Nam Á',
        path: '/landtours',
        icon: MapPin,
        category: 'module',
        deptBadge: 'Đối tác',
        keywords: 'ncc land tour landtour doi tac dia phuong dai ly noi dia quoc te guide dia phuong'
    },
    {
        id: 'insurances',
        title: 'NCC: Bảo hiểm Du lịch (Insurances)',
        subtitle: 'Chính sách bảo hiểm du lịch quốc tế, mức bồi thường và quyền lợi khách',
        path: '/insurances',
        icon: Shield,
        category: 'module',
        deptBadge: 'Đối tác',
        keywords: 'ncc bao hiem insurances bao hiem du lich quyen loi boi thuong muc bao hiem chubb bảo việt'
    },
    {
        id: 'visas',
        title: 'Dịch vụ Visa & Thị thực',
        subtitle: 'Quản lý tiến độ làm visa cho khách, nộp hồ sơ lãnh sự, kết quả visa',
        path: '/visas',
        icon: FileText,
        category: 'module',
        deptBadge: 'Visa',
        keywords: 'dich vu visa thi thuc ho chieu lam visa bao gia ho so visa to khai phong van'
    },
    {
        id: 'visa-providers',
        title: 'NCC: Đối tác cấp Visa',
        subtitle: 'Các trung tâm tiếp nhận thị thực, công ty dịch vụ visa ủy thác',
        path: '/visa-providers',
        icon: Building,
        category: 'module',
        deptBadge: 'Visa',
        keywords: 'ncc visa nha cung cap doi tac dich vu visa su quan lanh su quan vfs tls'
    },
    {
        id: 'visa-products',
        title: 'Sản phẩm Visa các nước',
        subtitle: 'Bảng giá và điều kiện xin visa Trung Quốc, Nhật, Hàn, Châu Âu, Mỹ',
        path: '/visa-products',
        icon: MapPin,
        category: 'module',
        deptBadge: 'Visa',
        keywords: 'san pham visa cac loai visa bang gia visa trung quoc chau au nhat han my uc phi visa'
    },
    {
        id: 'visa-form-templates',
        title: 'Mẫu khai Visa (Form Templates)',
        subtitle: 'Mẫu tờ khai xin visa các nước, biểu mẫu chuẩn bị hồ sơ chứng minh tài chính',
        path: '/visa-form-templates',
        icon: FileText,
        category: 'module',
        deptBadge: 'Visa',
        keywords: 'mau khai visa to khai form template don xin visa bieu mau khai ho so chung minh'
    },

    // ── Khối Tài chính & Kế toán ──
    {
        id: 'accountants',
        title: 'Quản trị Kế toán & Sổ sách',
        subtitle: 'Sổ quỹ thu chi, báo cáo công nợ nhà cung cấp, lợi nhuận đoàn',
        path: '/accountants',
        icon: DollarSign,
        category: 'module',
        deptBadge: 'Kế toán',
        requiredRole: 'admin',
        keywords: 'quan tri ke toan accountants so sach thu chi bao cao tai chinh loi nhuan cong no so quy so tien'
    },
    {
        id: 'vouchers',
        title: 'Ủy nhiệm chi (Vouchers / UNC)',
        subtitle: 'Lập lệnh chi ngân hàng, ủy nhiệm thanh toán tiền nhà cung cấp',
        path: '/vouchers',
        icon: FileText,
        category: 'module',
        deptBadge: 'Kế toán',
        keywords: 'uy nhiem chi vouchers ngan hang unc chuyen khoan uy nhiem thanh toan tien lenh chi'
    },
    {
        id: 'payment-vouchers',
        title: 'Phiếu chi & Thanh toán tiền mặt',
        subtitle: 'Quản lý tạm ứng công tác, hoàn ứng tour, chi tiêu nội bộ văn phòng',
        path: '/payment-vouchers',
        icon: FileText,
        category: 'module',
        deptBadge: 'Kế toán',
        keywords: 'phieu chi thanh toan payment vouchers chung tu tien mat hoa don tam ung hoan ung chi phi vat'
    },

    // ── Khối Giao tiếp & Marketing ──
    {
        id: 'inbox',
        title: 'Messenger / Facebook Chat',
        subtitle: 'Hộp thư trực chat fanpage Facebook, trả lời tin nhắn khách trực tiếp',
        path: '/inbox',
        icon: MessageSquare,
        category: 'module',
        deptBadge: 'Truyền thông',
        keywords: 'messenger facebook chat inbox tin nhan fanpage fb tuong tac cskh truc page chatbox'
    },
    {
        id: 'zalo-sandbox',
        title: 'Zalo Sandbox (Chat OA & Test)',
        subtitle: 'Kênh chat thử nghiệm Zalo Official Account, webhook và bot',
        path: '/zalo-sandbox',
        icon: MessageSquare,
        category: 'module',
        deptBadge: 'Truyền thông',
        keywords: 'zalo sandbox chat oa tin nhan bot webhook test zalo mini app tin zalo'
    },
    {
        id: 'zalo-ai-settings',
        title: 'Cấu hình Zalo AI Bot',
        subtitle: 'Thiết lập prompt, kịch bản trả lời tự động thông minh bằng AI',
        path: '/zalo-ai-settings',
        icon: Bot,
        category: 'module',
        deptBadge: 'AI & Bot',
        keywords: 'zalo ai bot cau hinh chatgpt tro ly tu dong tin nhan prompt auto bot'
    },
    {
        id: 'message-templates',
        title: 'Mẫu tin nhắn (Templates trả lời nhanh)',
        subtitle: 'Kho câu trả lời nhanh mẫu, kịch bản tư vấn chốt sale qua Zalo & SMS',
        path: '/message-templates',
        icon: MessageSquare,
        category: 'module',
        deptBadge: 'Truyền thông',
        keywords: 'mau tin nhan message templates tra loi nhanh sms zalo kich ban chat tin nhan mau'
    },
    {
        id: 'email',
        title: 'Hộp thư Email Nội bộ',
        subtitle: 'Hộp thư điện tử Zoho / Outlook, gửi nhận thư công việc',
        path: '/email',
        icon: Mail,
        category: 'module',
        deptBadge: 'Truyền thông',
        keywords: 'email mail hop thu outlook zoho thu den thu di send mail webmail hop thu'
    },
    {
        id: 'email-groups',
        title: 'Nhóm Email (Email Groups)',
        subtitle: 'Quản lý các hòm thư nhóm, danh sách phân phối mail phòng ban',
        path: '/email-groups',
        icon: Mail,
        category: 'module',
        deptBadge: 'Truyền thông',
        keywords: 'nhom email email groups danh sach mail hop thu chung mail phong ban danh sach mail'
    },
    {
        id: 'email-rules',
        title: 'Cấu hình Rules Email tự động',
        subtitle: 'Quy tắc lọc thư, chuyển tiếp tự động và gắn nhãn theo từ khóa',
        path: '/email-rules',
        icon: Settings,
        category: 'module',
        deptBadge: 'Truyền thông',
        keywords: 'cau hinh rules email quy tac mail tu dong forward loc thu nhan tin'
    },
    {
        id: 'marketing-ads',
        title: 'Quản trị Meta Ads (Data/KPI Chi phí)',
        subtitle: 'Báo cáo ngân sách quảng cáo Facebook, chi phí CPL, CPA theo từng BU',
        path: '/marketing-ads',
        icon: DollarSign,
        category: 'module',
        deptBadge: 'Marketing',
        keywords: 'chi phi marketing ads bao cao quang cao facebook meta ngan sach cpa cpl phan bo ngan sach chay ads'
    },
    {
        id: 'marketing-planning',
        title: 'Kế Hoạch & Dự Toán Thị Trường BU4',
        subtitle: 'Dự toán ngân sách Ads tuyến Bhutan, Ladakh, Sri Lanka',
        path: '/marketing-ads?subtab=planning',
        icon: BarChart3,
        category: 'module',
        deptBadge: 'Marketing',
        keywords: 'ke hoach thi truong du toan marketing ads bu4 bhutan ladakh sri lanka 30 trieu'
    },
    {
        id: 'travel-support',
        title: 'Hỗ trợ Khách khẩn cấp (Travel Support 24/7)',
        subtitle: 'Đường dây nóng xử lý sự cố tour, hỗ trợ khách hàng khẩn cấp tại điểm đến',
        path: '/travel-support',
        icon: Phone,
        category: 'module',
        deptBadge: 'CSKH',
        keywords: 'ho tro khach travel support cskh hotline cap cuu 24/7 su co khan cap bao hiem ho tro'
    },

    // ── Khối Hệ thống & Quản trị cấp cao ──
    {
        id: 'settings',
        title: 'Cấu hình Hệ thống Chung',
        subtitle: 'Thiết lập thông tin công ty, logo, tham số vận hành CRM',
        path: '/settings',
        icon: Settings,
        category: 'module',
        deptBadge: 'Admin',
        requiredRole: 'admin',
        keywords: 'cau hinh he thong settings cai dat chung cau hinh crm thiet lap'
    },
    {
        id: 'market-settings',
        title: 'Cấu hình Thị trường (Markets & Tuyến)',
        subtitle: 'Phân nhóm thị trường theo Business Unit (BU1 đến BU5), phân tuyến tour',
        path: '/market-settings',
        icon: Settings,
        category: 'module',
        deptBadge: 'Admin',
        requiredRole: 'admin',
        keywords: 'cau hinh thi truong markets bu tuyen tour dia ban phan khu bu1 bu2 bu3 bu4 bu5'
    },
    {
        id: 'media-settings',
        title: 'Quản lý Media & Banner Giao diện',
        subtitle: 'Kho hình ảnh, banner ứng dụng, hình ảnh quảng bá tour',
        path: '/media-settings',
        icon: Settings,
        category: 'module',
        deptBadge: 'Admin',
        keywords: 'quan ly media giao dien banner thu vien anh hinh anh upload kho anh'
    },
    {
        id: 'teams',
        title: 'Quản lý Nhóm & Phòng ban (Teams)',
        subtitle: 'Thiết lập danh sách team bán hàng, phân công trưởng nhóm',
        path: '/teams',
        icon: Users,
        category: 'module',
        deptBadge: 'Admin',
        keywords: 'quan ly nhom teams phong ban phan nhom team leader thanh vien'
    },
    {
        id: 'audit-logs',
        title: 'Nhật ký hệ thống (Audit Logs)',
        subtitle: 'Lịch sử thao tác người dùng, truy vết sửa/xóa dữ liệu quan trọng',
        path: '/audit-logs',
        icon: Activity,
        category: 'module',
        deptBadge: 'Admin',
        requiredRole: 'admin',
        keywords: 'nhat ky he thong audit logs lich su truy vet thao tac log xoa log sua bao mat'
    },
    {
        id: 'agent-manager',
        title: 'Quản lý AI Agent & Trợ lý',
        subtitle: 'Quản lý danh sách các agent thông minh tự động hóa trong CRM',
        path: '/agent-manager',
        icon: Bot,
        category: 'module',
        deptBadge: 'Admin',
        requiredRole: 'admin',
        keywords: 'quan ly ai agent tro ly thong minh bot openai anthropic gemini copilot'
    },
    {
        id: 'bu-rules',
        title: 'Quy tắc Business Unit (BU Rules)',
        subtitle: 'Luật phân bổ Lead tự động theo từ khóa chiến dịch cho từng BU',
        path: '/bu-rules',
        icon: Shield,
        category: 'module',
        deptBadge: 'Admin',
        requiredRole: 'admin',
        keywords: 'quy tac business unit bu rules phan bo lead tu khoa marketing ngan sach bu1 bu2 bu3 bu4 bu5'
    },
    {
        id: 'management-dashboard',
        title: 'Dashboard Quản lý Toàn diện (CEO)',
        subtitle: 'Chỉ số KPI, doanh thu, lợi nhuận gộp toàn công ty dành cho Ban Giám Đốc',
        path: '/management-dashboard',
        icon: LayoutTemplate,
        category: 'module',
        deptBadge: 'BOD',
        requiredRole: 'admin',
        keywords: 'dashboard quan ly ceo giam doc tong quan doanh nghiep doanh thu loi nhuan kpi toan cong ty bod'
    },
    {
        id: 'ceo-departures-dashboard',
        title: 'Dashboard Điều hành Tổng quan (CEO)',
        subtitle: 'Bức tranh toàn cảnh các đoàn đang chạy, đoàn sắp khởi hành',
        path: '/ceo-departures-dashboard',
        icon: LayoutTemplate,
        category: 'module',
        deptBadge: 'BOD',
        requiredRole: 'admin',
        keywords: 'dashboard dieu hanh ceo giam doc khoi hanh tong quan tinh hinh tour tinh trang cho'
    }
];

// ── 3. TÀI LIỆU, SOP, CẨM NANG & LIÊN KẾT NGOÀI (TIER 3) ──
export const documentationItems = [
    {
        id: 'huong-dan-tao-nghi-phep',
        title: 'Hướng dẫn tạo đơn nghỉ phép trên ERP',
        subtitle: 'Quy trình nộp đơn, cách tính ca sáng/chiều, theo dõi duyệt phép',
        path: '/huong-dan-erp/tao-nghi-phep',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'huong dan tao don nghi phep tren erp cach xin nghi quy trinh nop don xem phep ton huong dan su dung xin nghi off'
    },
    {
        id: 'huong-dan-dat-phong-hop',
        title: 'Hướng dẫn đặt phòng họp trên ERP',
        subtitle: 'Cách xem lịch trống và đăng ký phòng họp đúng quy chuẩn',
        path: '/huong-dan-erp/dat-phong-hop',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'huong dan dat phong hop erp cach giu phong hop meeting room giu cho'
    },
    {
        id: 'doc-chinh-sach-danh-gia',
        title: 'SOP Chính Sách Đánh Giá (Review HDV)',
        subtitle: 'SOP-MKT-006 • Thưởng review 5 sao Google 100k - 150k cho Tour Leader & HDV',
        path: '/tai-lieu/chinh-sach-danh-gia',
        icon: Star,
        category: 'doc',
        type: 'doc',
        keywords: 'sop chinh sach danh gia review google maps hdv thuong 100k 150k marketing cskh quy che 5 sao'
    },
    {
        id: 'doc-tai-lieu',
        title: 'Trang Chủ Trung Tâm Tài Liệu (/tai-lieu)',
        subtitle: 'Thư viện SOP, cẩm nang bán hàng, biểu mẫu và quy trình công ty',
        path: '/tai-lieu',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'trang chu tai lieu noi bo sop cam nang hub huong dan quy trinh bieu mau'
    },
    {
        id: 'doc-hub-hdv',
        title: 'HUB Hướng Dẫn Viên',
        subtitle: 'Bàn làm việc của HDV — checklist, review đánh giá, SOP xử lý sự cố',
        path: '/hdv',
        icon: UserCheck,
        category: 'doc',
        type: 'doc',
        keywords: 'hub hdv huong dan vien ban lam viec danh gia review checklist tour leader'
    },
    {
        id: 'doc-quy-che-hdv',
        title: 'Quy Chế Lương Hướng Dẫn Viên',
        subtitle: 'Chính sách lương, thưởng, công tác phí, phụ cấp cho HDV FIT Tour',
        path: '/tai-lieu/quy-che-luong-hdv',
        icon: DollarSign,
        category: 'doc',
        type: 'doc',
        keywords: 'quy che luong hdv thuong cong tac phi phu cap huong dan vien'
    },
    {
        id: 'doc-sop-sales',
        title: 'SOP Sales & Quy Trình Workplace',
        subtitle: 'Hướng dẫn Sales nhận Lead, cập nhật ERP và tối ưu Workplace',
        path: '/tai-lieu/sop-sales',
        icon: Briefcase,
        category: 'doc',
        type: 'doc',
        keywords: 'sop sales ban hang quy trinh lead workplace chot deal'
    },
    {
        id: 'doc-sop-dieu-phoi',
        title: 'SOP Điều Phối Lead',
        subtitle: 'Quy trình tiếp nhận và phân bổ Lead dành cho Trung tâm Điều phối',
        path: '/tai-lieu/sop-dieu-phoi',
        icon: Navigation,
        category: 'doc',
        type: 'doc',
        keywords: 'sop dieu phoi lead tiep nhan chia lead phan bo sale'
    },
    {
        id: 'doc-tong-quan-lead',
        title: 'Tổng Quan Quy Trình Xử Lý Lead',
        subtitle: 'Bức tranh toàn cảnh về quy trình xử lý Lead từ Marketing đến Sales',
        path: '/tai-lieu/tong-quan-lead',
        icon: LayoutTemplate,
        category: 'doc',
        type: 'doc',
        keywords: 'tong quan quy trinh lead dieu phoi sale buc tranh toan canh'
    },
    {
        id: 'doc-quy-trinh-sale-op',
        title: 'Quy Trình Phối Hợp Sale & Điều Hành',
        subtitle: 'Nhắc nhở quy trình làm việc giữa Sale & Điều Hành và các lỗi sai thường gặp',
        path: '/tai-lieu/quy-trinh-sale-dieu-hanh',
        icon: Activity,
        category: 'doc',
        type: 'doc',
        keywords: 'quy trinh sale dieu hanh op phoi hop ban giao tour loi sai'
    },
    {
        id: 'doc-quy-trinh-thanh-toan',
        title: 'Quy Trình Thanh Toán & Quyết Toán Tour',
        subtitle: 'Quy trình thanh toán, bàn giao, quyết toán tour cho Kế toán & OP',
        path: '/tai-lieu/quy-trinh-thanh-toan-ban-giao-quyet-toan-tour',
        icon: DollarSign,
        category: 'doc',
        type: 'doc',
        keywords: 'quy trinh thanh toan ban giao quyet toan tour ke toan op chung tu tam ung hoan ung'
    },
    {
        id: 'doc-co-che-kpi',
        title: 'Cơ Chế Lương – KPI – Thưởng FIT Tour',
        subtitle: 'Quyết định ban hành cơ chế lương – KPI – thưởng và phúc lợi nhân sự',
        path: '/tai-lieu/co-che-kpi',
        icon: DollarSign,
        category: 'doc',
        type: 'doc',
        keywords: 'co che kpi luong thuong phuc loi nhan su ban giam doc che do nghi phep'
    },
    {
        id: 'doc-dat-ten-tour',
        title: 'SOP Chuẩn Hóa Tên Tour',
        subtitle: 'Quy chuẩn đặt tên tour trên ERP, Website và Social Media',
        path: '/tai-lieu/dat-ten-tour',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'sop chuan hoa dat ten tour erp website social media quy chuan'
    },
    {
        id: 'doc-zoho-email',
        title: 'Hướng Dẫn Cài Đặt Zoho Mail',
        subtitle: 'Hướng dẫn cài đặt Zoho Mail với Outlook, Apple Mail, Spark (IMAP)',
        path: '/tai-lieu/zoho-email',
        icon: Mail,
        category: 'doc',
        type: 'doc',
        keywords: 'huong dan cai dat zoho mail outlook apple mail spark imap ket noi'
    },
    {
        id: 'doc-bo-nguyen-tac',
        title: 'Bộ Nguyên Tắc Hành Xử Nhân Viên Văn Phòng',
        subtitle: 'Quy tắc ứng xử, giao tiếp, ra quyết định, xử lý sự cố, xin vắng mặt',
        path: '/tai-lieu/bo-nguyen-tac-hanh-xu-nhan-vien',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'bo nguyen tac hanh xu nhan vien van phong quy tac ung xu giao tiep van hoa noi quy'
    },
    {
        id: 'doc-rule-meta-ads',
        title: 'Rule Meta Ads FIT Tour',
        subtitle: 'Các quy tắc bắt buộc và khuyến nghị khi thiết lập chiến dịch Meta Ads',
        path: '/tai-lieu/rule-meta-ads',
        icon: Shield,
        category: 'doc',
        type: 'doc',
        keywords: 'rule meta ads facebook quang cao quy tac marketing'
    },
    {
        id: 'doc-blueprint-meta-ads',
        title: 'Blueprint Meta Ads',
        subtitle: 'Hướng dẫn chạy quảng cáo Meta chuẩn FIT Tour - Quy tắc đặt tên, target, content',
        path: '/tai-lieu/blueprint-meta-ads',
        icon: LayoutTemplate,
        category: 'doc',
        type: 'doc',
        keywords: 'blueprint meta ads facebook quang cao marketing target content'
    },
    {
        id: 'doc-sop-meta-ads',
        title: 'SOP Quy Ước Đặt Tên Meta Ads',
        subtitle: 'Quy chuẩn đặt tên chiến dịch, nhóm quảng cáo và mẫu quảng cáo',
        path: '/tai-lieu/sop-meta-ads',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'sop quy uoc dat ten meta ads campaign adset naming convention'
    },
    {
        id: 'doc-brand-guideline',
        title: 'Brand Identity Guideline',
        subtitle: 'Tài liệu hướng dẫn nhận diện thương hiệu FIT Tour (logo, màu sắc, font chữ...)',
        path: '/tai-lieu/brand-guideline',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'brand identity guideline nhan dien thuong hieu logo font mau sac'
    },
    {
        id: 'doc-cam-nang-thuong-hieu',
        title: 'Cẩm Nang Thương Hiệu FIT Tour',
        subtitle: 'Truly Experiences • Giá trị cốt lõi và định vị thương hiệu',
        path: '/cam-nang-thuong-hieu',
        icon: Star,
        category: 'doc',
        type: 'doc',
        keywords: 'cam nang thuong hieu truly experiences fit tour phong cach'
    },
    {
        id: 'doc-tu-van-cuu-trai-cau',
        title: 'Cẩm Nang Chốt Sale Cửu Trại Câu (BU1)',
        subtitle: 'Kịch bản chat, FAQ cọc 50/50, visa đoàn, SIM data và cẩm nang tư vấn',
        path: '/tai-lieu/tu-van-cuu-trai-cau',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'cam nang chot sale cuu trai cau bu1 trung quoc thanh do tu van khach hang'
    },
    {
        id: 'doc-tu-van-ladakh',
        title: 'Cẩm Nang Chốt Sale Ladakh (BU4)',
        subtitle: 'Cẩm nang tư vấn và chốt sale tuyến tour Ladakh Ấn Độ',
        path: '/tai-lieu/tu-van-ladakh-bu4',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'cam nang chot sale ladakh bu4 an do tu van khach hang'
    },
    {
        id: 'doc-tu-van-bhutan',
        title: 'Cẩm Nang Chốt Sale Bhutan (5N4Đ)',
        subtitle: 'Cẩm nang tư vấn và chốt sale tuyến tour vương quốc hạnh phúc Bhutan',
        path: '/tai-lieu/tu-van-bhutan-5n4d',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'cam nang chot sale bhutan 5n4d tu van tour'
    },
    {
        id: 'doc-thi-truong-bu1',
        title: 'Phân Tích Thị Trường & Dự Toán Ads BU1 (Tour Trung Quốc)',
        subtitle: 'Dữ liệu lịch sử, CPL & Dự toán Ads Giang Nam, Bắc Kinh, Á Đinh, Tân Cương, Thanh Tạng, Lệ Giang, Cáp Nhĩ Tân',
        path: '/tai-lieu/thi-truong-bu1',
        icon: Activity,
        category: 'doc',
        type: 'doc',
        keywords: 'thi truong bu1 tour trung quoc du toan ads giang nam bac kinh dao thanh a dinh tan cuong thanh tang le giang cap nhi tan cpl lead ngan sach'
    },
    {
        id: 'doc-thi-truong-bu2',
        title: 'Phân Tích Thị Trường & Dự Toán Ads BU2 (Đông Bắc Á: Nhật Bản, Hàn Quốc, Đài Loan)',
        subtitle: 'Kế hoạch Q4/2026: Mô hình Liên minh & Độc quyền Hokkaido',
        path: '/tai-lieu/thi-truong-bu2',
        icon: Activity,
        category: 'doc',
        type: 'doc',
        keywords: 'thi truong bu2 du toan ads dong bac a nhat ban hokkaido han quoc dai loan cung duong vang lien minh consortium cpl lead ngan sach'
    },
    {
        id: 'doc-thi-truong-bu3',
        title: 'Phân Tích Thị Trường & Dự Toán Ads BU3 (B2B/MICE)',
        subtitle: 'Kế hoạch 12 tháng, chạy đón đầu 2 tháng, phân bổ 150M & Máy tính phễu chuyển đổi đoàn',
        path: '/tai-lieu/thi-truong-bu3',
        icon: Activity,
        category: 'doc',
        type: 'doc',
        keywords: 'thi truong bu3 du toan ads tour doan doanh nghiep mice teambuilding company trip year end party hoa anh dao cpl lead ngan sach 150 trieu'
    },
    {
        id: 'doc-thi-truong-bu4',
        title: 'Phân Tích Thị Trường & Dự Toán Ads BU4',
        subtitle: 'Obsidian Doc: Dữ liệu lịch sử, CPL & Máy tính dự toán ngân sách Ads Bhutan, Ladakh, Sri Lanka',
        path: '/tai-lieu/thi-truong-bu4',
        icon: Activity,
        category: 'doc',
        type: 'doc',
        keywords: 'thi truong bu4 du toan ads bhutan ladakh sri lanka may tinh cpl lead ngan sach'
    },
    {
        id: 'doc-thi-truong-bu5',
        title: 'Phân Tích Thị Trường & Dự Toán Ads BU5 (Khám Phá & Độc Bản)',
        subtitle: 'Kế hoạch 5 tuyến độc bản: Murmansk Bắc Cực Quang (Nga), Ai Cập Sông Nile, Ma Rốc, Pakistan, Trung Á',
        path: '/tai-lieu/thi-truong-bu5',
        icon: Activity,
        category: 'doc',
        type: 'doc',
        keywords: 'thi truong bu5 du toan ads pakistan ai cap maroc murmansk nga cuc quang trung a to lua cpl lead ngan sach'
    },
    {
        id: 'doc-bieu-mau',
        title: 'Biểu Mẫu Hành Chính & Giấy Phép',
        subtitle: 'Giấy phép kinh doanh GPKD, GPLH quốc tế, biểu mẫu nội bộ',
        path: '/tai-lieu/bieu-mau',
        icon: FileText,
        category: 'doc',
        type: 'doc',
        keywords: 'bieu mau hanh chinh giay phep kinh doanh gpkd gplh quoc te van ban phap ly'
    },
    {
        id: 'doc-manual',
        title: 'Sổ Tay Hướng Dẫn Sử Dụng ERP (Manual)',
        subtitle: 'Sổ tay hướng dẫn chi tiết các tính năng trên hệ thống FIT Tour CRM',
        path: '/manual/overview',
        icon: BookOpen,
        category: 'doc',
        type: 'doc',
        keywords: 'so tay huong dan su dung erp manual crm tinh nang toan tap'
    },
    // ── External Links ──
    {
        id: 'ext-huong-dan-danh-gia',
        title: 'Hướng Dẫn Đánh Giá FIT TOUR Trên Google Maps',
        subtitle: 'fittour.vn/huong-dan-danh-gia-fit-tour • Hướng dẫn khách hàng đánh giá 5 sao kèm hình ảnh',
        path: 'https://fittour.vn/huong-dan-danh-gia-fit-tour',
        type: 'external',
        category: 'doc',
        icon: Star,
        keywords: 'huong dan danh gia google maps review 5 sao fit tour hdv tour leader khach hang viet nhan xet link guide map'
    },
    {
        id: 'ext-google-maps-fit-tour',
        title: 'Link Google Maps FIT TOUR (Gửi khách đánh giá)',
        subtitle: 'Trang Google Maps chính thức của FIT TOUR để khách hàng để lại Review 5 sao',
        path: 'https://maps.app.goo.gl/wJkP1N3yqP1M3zQ66',
        type: 'external',
        category: 'doc',
        icon: MapPin,
        keywords: 'link google maps fit tour cham sao review viet danh gia truc tiep dia diem'
    },
    {
        id: 'ext-website-fittour',
        title: 'Website FIT TOUR Chính Thức',
        subtitle: 'fittour.vn • Du lịch trải nghiệm có Guu',
        path: 'https://fittour.vn',
        type: 'external',
        category: 'doc',
        icon: Navigation,
        keywords: 'website fittour trang chu web fit tour'
    },
    {
        id: 'ext-checklist-thiet-ke-tour',
        title: 'Checklist Thiết Kế Tour Mới (Google Sheets)',
        subtitle: 'Bảng checklist thiết kế và chuẩn bị sản phẩm tour mới của phòng Điều Hành',
        path: 'https://docs.google.com/spreadsheets/d/1GcDo5omS19co79Gv3vQ-Naf9v2TudWf9LmMr_w8LFvQ/edit?gid=0#gid=0',
        type: 'external',
        category: 'doc',
        icon: FileText,
        keywords: 'checklist thiet ke tour san pham moi dieu hanh google sheet'
    }
];

// ── 4. GỢI Ý THÔNG MINH KHI KHÔNG TÌM THẤY KẾT QUẢ (SMART SUGGESTION CHIPS) ──
export const smartSuggestionChips = [
    { label: '⚡ Tạo đơn xin nghỉ', query: 'nghỉ phép' },
    { label: '🎂 Sinh nhật nhân viên', query: 'sinh nhật' },
    { label: '🏢 Đặt phòng họp', query: 'phòng họp' },
    { label: '🎁 Sinh nhật khách hàng', query: 'sinh nhật khách' },
    { label: '⭐ Đánh giá Google Maps', query: 'đánh giá' },
    { label: '📖 Sổ tay hướng dẫn ERP', query: 'sổ tay' }
];

export const allCatalogItems = [
    ...actionShortcuts,
    ...coreModules,
    ...documentationItems
];

